/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { Effect, InventoryItemWithAvailable, Setter } from "../../../utils/Interfaces";
import { NavigateBefore } from "@mui/icons-material";
import React from "react";
import { NumericFormat } from "react-number-format";
import { customScrollbarStyle } from "../../../utils/Functions";

export interface InventoryEgressModalProps {
    inventory: InventoryItemWithAvailable;
    amount: number;
    setAmount: Setter<number>;
    notes: string;
    setNotes: Setter<string>;
    submitting: boolean;
    submitLabel: string;
    progressDialog: Effect;
    regressDialog: Effect;
}

function InventorySelector(props: InventoryEgressModalProps) {
    const { inventory, amount, setAmount, notes, setNotes, submitting, submitLabel, progressDialog, regressDialog } = props;

    function getFormErrors(): string {
        if (amount === 0) {
            return "Material count must be at least 1.";
        }

        if (inventory.available < amount) {
            return "Not enough material available.";
        }

        if (isNaN(amount)) {
            return "Material count must be at least 1.";
        }

        return "";
    }

    return (
        <React.Fragment>
            <Box sx={{ mb: 2 }} />

            {getFormErrors() !== "" && (
                <Alert severity="error" sx={{ mb: 3, mt: 3 }}>
                    {getFormErrors()}
                </Alert>
            )}

            {getFormErrors() === "" && (
                <Alert severity="success" sx={{ mb: 3, mt: 3 }}>
                    No errors detected.
                </Alert>
            )}

            <Box sx={{ pr: 2, overflowY: "auto", maxHeight: "50vh", ...customScrollbarStyle() }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1, ml: 1, mr: 1 }}>
                    <Typography fontWeight="bold">Part/Material</Typography>
                    <Typography textAlign="right">{inventory?.material.name ?? "--"}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1, ml: 1, mr: 1 }}>
                    <Typography fontWeight="bold">P/N</Typography>
                    <Typography textAlign="right">{inventory?.material.part_number ?? "--"}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1, ml: 1, mr: 1 }}>
                    <Typography fontWeight="bold">Amt. Available</Typography>
                    <Typography sx={{ mb: 1 }} textAlign="right">
                        {(inventory as any)?.available ?? "--"} Available
                    </Typography>
                </Stack>

                <Box sx={{ mb: 2 }} />

                <NumericFormat
                    disabled={inventory === null}
                    value={amount}
                    customInput={TextField}
                    style={{ marginLeft: 0, marginTop: 0, width: "100%" }}
                    label="Amount"
                    thousandSeparator=","
                    decimalScale={0}
                    allowNegative={false}
                    allowLeadingZeros={false}
                    onValueChange={(values) => {
                        setAmount(parseInt(values.value));
                    }}
                />

                <TextField
                    multiline
                    rows={7}
                    fullWidth
                    margin="normal"
                    id="notes"
                    name="notes"
                    value={notes}
                    label="Technician Notes"
                    size="small"
                    onChange={(event) => setNotes(event.target.value)}
                    sx={{ mt: 3.5, mb: 1 }}
                />
            </Box>

            <Stack direction="row" justifyContent="space-between">
                <Button onClick={regressDialog} sx={{ mt: 2, mb: -2 }}>
                    <NavigateBefore />
                    &nbsp;Prev
                </Button>

                {!submitting && (
                    <Button
                        disabled={getFormErrors() !== ""}
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={progressDialog}
                        sx={{ mt: 2, mb: -2 }}
                    >
                        {submitLabel}
                    </Button>
                )}

                {submitting && <CircularProgress size={16} sx={{ mt: 3.5, mr: 1, mb: -1 }} />}
            </Stack>
        </React.Fragment>
    );
}

export default InventorySelector;
